import { BookOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/ui';
import { createFileRoute, Link, linkOptions, Navigate, redirect } from '@tanstack/react-router';
import { CrmPrimaryNavigation } from '@pages/crm';
import { useUserProfileContext } from '@entities/user-profile';
import { useAuth } from '@features/auth';
import { useAbility } from '@features/authorization';
import { HelpdeskList, helpdeskSearchSchema } from '@features/helpdesk';
import type { RouterContext } from '@shared/config/router-context';
import {
  canAccessHelpdesk,
  getHelpdeskKnowledgePath,
  canAccessCrmCustomerDirectory,
  canAccessCrmCustomerMembershipDirectory,
} from '@shared/lib';

export const Route = createFileRoute('/$locale/_authenticated/crm/helpdesk/')({
  validateSearch: helpdeskSearchSchema,
  beforeLoad: ({ context, params, search }) => {
    const { auth, ability } = context as RouterContext;
    if (auth.isLoading || !auth.isAuthenticated) return;
    if (!canAccessHelpdesk(ability)) {
      const knowledgePath = getHelpdeskKnowledgePath(ability);
      if (knowledgePath) {
        // eslint-disable-next-line @typescript-eslint/only-throw-error
        throw redirect({ to: knowledgePath, params: { locale: params.locale }, search });
      }
      // eslint-disable-next-line @typescript-eslint/only-throw-error
      throw redirect({ to: '/$locale/unauthorized', params: { locale: params.locale } });
    }
  },
  component: HelpdeskRoute,
});

function HelpdeskRoute() {
  const { locale } = Route.useParams();
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const ability = useAbility();
  const { user, permissions } = useAuth();
  const profileContext = useUserProfileContext(user?.id ?? '');
  const knowledgePath = getHelpdeskKnowledgePath(ability);
  const getRequestLink = (taskUuid: string) =>
    linkOptions({
      to: '/$locale/crm/helpdesk/$taskUuid',
      params: { locale, taskUuid },
      search,
    });
  if (!canAccessHelpdesk(ability))
    return knowledgePath ? (
      <Navigate to={knowledgePath} params={{ locale }} search={search} replace />
    ) : (
      <Navigate to="/$locale/unauthorized" params={{ locale }} replace />
    );
  return (
    <main className="min-w-0 p-4 pb-36 sm:p-6 sm:pb-24">
      <CrmPrimaryNavigation
        activeSection="helpdesk"
        canAccessHelpdesk
        canAccessProfiles={canAccessCrmCustomerDirectory(ability)}
        canAccessMemberships={canAccessCrmCustomerMembershipDirectory(ability)}
        locale={locale}
      />
      <HelpdeskList
        knowledgeLink={
          knowledgePath ? (
            <Button variant="outline" asChild>
              <Link to={knowledgePath} params={{ locale }} search={search}>
                <BookOpen aria-hidden="true" />
                {t('aiContent.knowledge')}
              </Link>
            </Button>
          ) : undefined
        }
        canCreate={ability.can('create', 'HelpdeskTask')}
        canUpdate={ability.can('update', 'HelpdeskTask')}
        canDelete={ability.can('delete', 'HelpdeskTask')}
        viewerProfileId={profileContext.data?.data.profile_ref}
        viewerAccessScope={user ? `${user.id}:${JSON.stringify(permissions ?? [])}` : ''}
        search={search}
        onSearchChange={(next) => {
          void navigate({ search: next, replace: true, resetScroll: false });
        }}
        getRequestLink={getRequestLink}
        onOpen={(taskUuid) => {
          void navigate(getRequestLink(taskUuid));
        }}
      />
    </main>
  );
}
